/**
 * Pure helpers behind {@link TimeInput}. They live in a companion `.ts` because
 * a `.tsx` that exports anything but components breaks Vite Fast Refresh (see
 * apps/frontend/CLAUDE.md).
 */

/** A named shortcut rendered in the optional third dropdown. */
export interface TimeInputPreset {
  label: string
  /** 24-hour `HH:mm`. */
  value: string
}

/**
 * Accepts what every previous time field accepted, so no stored value is lost
 * in the migration: bare or zero-padded 24-hour (`4:00`, `09:30`, `20:05`) and
 * the 12-hour forms the replay picker's free-text box took (`4:00 PM`,
 * `12:30am`). Returns null for anything out of range: callers show the
 * placeholder rather than inventing a time.
 */
const CLOCK_RE = /^\s*(\d{1,2})\s*:\s*(\d{2})\s*(am|pm)?\s*$/i

export function parseHhMm(
  text: string | null | undefined,
): { hour: number; minute: number } | null {
  const m = CLOCK_RE.exec(text ?? '')
  if (!m) return null
  let hour = Number(m[1])
  const minute = Number(m[2])
  const meridiem = m[3]?.toLowerCase()
  if (minute > 59) return null
  if (meridiem) {
    if (hour < 1 || hour > 12) return null
    if (meridiem === 'pm') hour = hour === 12 ? 12 : hour + 12
    else hour = hour === 12 ? 0 : hour
  } else if (hour > 23) {
    return null
  }
  return { hour, minute }
}

/** The canonical stored form: zero-padded 24-hour. */
export function toHhMm(hour: number, minute: number): string {
  return `${String(hour).padStart(2, '0')}:${String(minute).padStart(2, '0')}`
}

/**
 * The query form of a stored clock time. The history views type these columns
 * TIME, and MotherDuck's Postgres-wire endpoint decodes a TIME parameter
 * strictly (`cannot decode 15:00 into Time`, verified 2026-09-12), so values
 * sent to the backend carry seconds. Non-strings and anything that is not a
 * bare clock string (already `HH:mm:ss`, a column reference) pass through.
 */
export function toHhMmSs(value: unknown): unknown {
  if (typeof value !== 'string') return value
  const parsed = parseHhMm(value)
  if (!parsed) return value
  return `${toHhMm(parsed.hour, parsed.minute)}:00`
}
